import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { useTheme } from '@/context/ThemeContext';
import { supabase } from '@/lib/supabase';

interface SessionLog {
  id: string;
  action: string;
  municipio_nome: string | null;
  ip_address: string | null;
  created_at: string;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

export default function PerfilPage() {
  const { profile, user } = useAuth();
  const { activeMunicipio } = useActiveMunicipio();
  const { theme } = useTheme();

  // Dados do perfil
  const [fullName, setFullName] = useState(profile?.full_name ?? '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [passMsg, setPassMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Logs de sessão
  const [sessionLogs, setSessionLogs] = useState<SessionLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [logsError, setLogsError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFullName(profile?.full_name ?? '');
  }, [profile?.full_name]);

  const loadLogs = async () => {
    if (!user?.id) return;
    setLoadingLogs(true);
    setLogsError(null);
    const { data, error: err } = await supabase
      .from('session_logs')
      .select('id, action, municipio_nome, ip_address, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30);
    if (err) setLogsError(err.message);
    setSessionLogs(data ?? []);
    setLoadingLogs(false);
  };

  useEffect(() => {
    if (user?.id) loadLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) return;
    setSavingName(true);
    setNameMsg(null);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim(), updated_at: new Date().toISOString() })
        .eq('id', user?.id ?? '');
      if (error) throw new Error(error.message);
      setNameMsg({ type: 'success', text: 'Nome atualizado com sucesso!' });
    } catch (err) {
      setNameMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao guardar.' });
    } finally {
      setSavingName(false);
      setTimeout(() => setNameMsg(null), 4000);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPassMsg(null);
    if (newPassword.length < 6) {
      setPassMsg({ type: 'error', text: 'A nova password deve ter pelo menos 6 caracteres.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPassMsg({ type: 'error', text: 'As passwords não coincidem.' });
      return;
    }
    setSavingPass(true);
    try {
      // Re-autenticar com a password atual
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: profile?.email ?? user?.email ?? '',
        password: currentPassword,
      });
      if (signInErr) throw new Error('Password atual incorreta.');

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw new Error(error.message);

      setPassMsg({ type: 'success', text: 'Password alterada com sucesso!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPassMsg({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao alterar password.' });
    } finally {
      setSavingPass(false);
      setTimeout(() => setPassMsg(null), 5000);
    }
  };

  const roleLabel: Record<string, string> = {
    super_admin: 'Super Admin',
    admin: 'Administrador',
    viewer: 'Visualizador',
  };

  const roleBadge: Record<string, string> = {
    super_admin: 'bg-zinc-900 text-white',
    admin: 'bg-amber-100 text-amber-800',
    viewer: 'bg-zinc-100 text-zinc-600',
  };

  const initials = (profile?.full_name || profile?.email || '')
    .split(/[\s@]/).filter(Boolean).slice(0, 2)
    .map((n: string) => n[0]?.toUpperCase() ?? '').join('');

  return (
    <div className="min-h-screen bg-[#f5f5f3]">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold flex-shrink-0"
              style={{ backgroundColor: `${theme.sidebarAccent}20`, color: theme.sidebarAccent }}
            >
              {initials || <i className="ri-user-line" />}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">O meu perfil</span>
                {profile?.role && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${roleBadge[profile.role] ?? roleBadge['viewer']}`}>
                    {roleLabel[profile.role] ?? profile.role}
                  </span>
                )}
              </div>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight">
                {profile?.full_name || profile?.email || 'Utilizador'}
              </h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">
                {profile?.email}
                {activeMunicipio && (
                  <span className="ml-2 text-zinc-300">·</span>
                )}
                {activeMunicipio && (
                  <span className="ml-2">{activeMunicipio.nome}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">

        {/* ── Informações Pessoais ── */}
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
              <i className="ri-user-settings-line text-zinc-600 text-sm" />
            </div>
            <div>
              <h2 className="text-[14px] font-semibold text-zinc-800">Informações Pessoais</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Atualiza o teu nome de apresentação</p>
            </div>
          </div>
          <form onSubmit={handleSaveName} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Nome completo
                </label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="O teu nome"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 focus:border-zinc-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={profile?.email ?? user?.email ?? ''}
                  disabled
                  className="w-full px-3 py-2.5 text-sm border border-zinc-100 rounded-lg bg-zinc-50 text-zinc-400 cursor-not-allowed"
                />
                <p className="text-[11px] text-zinc-400 mt-1">O email não pode ser alterado aqui.</p>
              </div>
            </div>

            {/* Info extra — só leitura */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-zinc-50 rounded-xl">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-shield-user-line text-zinc-400 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400 font-medium">Permissão</p>
                  <p className="text-[12px] font-semibold text-zinc-700">{roleLabel[profile?.role ?? 'viewer'] ?? profile?.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-zinc-50 rounded-xl">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-building-4-line text-zinc-400 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400 font-medium">Módulo</p>
                  <p className="text-[12px] font-semibold text-zinc-700 truncate">{activeMunicipio?.nome ?? '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-zinc-50 rounded-xl">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-calendar-line text-zinc-400 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400 font-medium">Membro desde</p>
                  <p className="text-[12px] font-semibold text-zinc-700">
                    {profile?.created_at
                      ? new Date(profile.created_at).toLocaleDateString('pt-PT', { month: 'short', year: 'numeric' })
                      : '—'}
                  </p>
                </div>
              </div>
            </div>

            {nameMsg && (
              <div className={`flex items-center gap-2 p-3 rounded-lg border text-[13px] ${nameMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <i className={`text-sm ${nameMsg.type === 'success' ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-error-warning-fill text-red-500'}`} />
                {nameMsg.text}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingName || !fullName.trim() || fullName.trim() === profile?.full_name}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 cursor-pointer"
              >
                {savingName
                  ? <><i className="ri-loader-4-line animate-spin text-sm" />A guardar...</>
                  : <><i className="ri-save-line text-sm" />Guardar Nome</>
                }
              </button>
            </div>
          </form>
        </div>

        {/* ── Alterar Password ── */}
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
              <i className="ri-lock-password-line text-zinc-600 text-sm" />
            </div>
            <div>
              <h2 className="text-[14px] font-semibold text-zinc-800">Alterar Password</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Precisas de saber a password atual para a alterar</p>
            </div>
          </div>
          <form onSubmit={handleChangePassword} className="p-6 space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
                Password atual
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="A tua password atual"
                  required
                  className="w-full pr-10 px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-zinc-400 hover:text-zinc-600 cursor-pointer"
                >
                  <i className={showPasswords ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Nova password
                </label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-colors"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Confirmar nova password
                </label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetir nova password"
                  required
                  className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900/20 transition-colors"
                />
              </div>
            </div>

            {/* Indicador de força */}
            {newPassword.length > 0 && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((level) => {
                    const strength = newPassword.length >= 12 ? 4 : newPassword.length >= 8 ? 3 : newPassword.length >= 6 ? 2 : 1;
                    return (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          level <= strength
                            ? strength === 1 ? 'bg-red-400' : strength === 2 ? 'bg-amber-400' : strength === 3 ? 'bg-teal-400' : 'bg-emerald-500'
                            : 'bg-zinc-100'
                        }`}
                      />
                    );
                  })}
                </div>
                <p className="text-[11px] text-zinc-400">
                  {newPassword.length < 6 ? 'Muito curta' : newPassword.length < 8 ? 'Fraca' : newPassword.length < 12 ? 'Boa' : 'Forte'}
                </p>
              </div>
            )}

            {passMsg && (
              <div className={`flex items-center gap-2 p-3 rounded-lg border text-[13px] ${passMsg.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <i className={`text-sm ${passMsg.type === 'success' ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-error-warning-fill text-red-500'}`} />
                {passMsg.text}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingPass || !currentPassword || !newPassword || !confirmPassword}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 cursor-pointer"
              >
                {savingPass
                  ? <><i className="ri-loader-4-line animate-spin text-sm" />A alterar...</>
                  : <><i className="ri-key-line text-sm" />Alterar Password</>
                }
              </button>
            </div>
          </form>
        </div>

        {/* ── Histórico de Sessões ── */}
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                <i className="ri-history-line text-zinc-600 text-sm" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-zinc-800">Histórico de Sessões</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">Últimos 30 acessos à aplicação</p>
              </div>
            </div>
            <button
              onClick={loadLogs}
              disabled={loadingLogs}
              className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-500 hover:text-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              <i className={`ri-refresh-line text-sm ${loadingLogs ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
          <div className="divide-y divide-zinc-50">
            {loadingLogs ? (
              <div className="flex items-center justify-center py-10">
                <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
              </div>
            ) : logsError ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-zinc-400">
                <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full">
                  <i className="ri-error-warning-line text-xl text-red-400" />
                </div>
                <p className="text-[13px] text-red-500">Erro ao carregar logs</p>
                <p className="text-[11px] text-zinc-400 font-mono">{logsError}</p>
                <button onClick={loadLogs} className="mt-1 text-[12px] text-zinc-500 underline cursor-pointer">Tentar novamente</button>
              </div>
            ) : sessionLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-zinc-400">
                <div className="w-10 h-10 flex items-center justify-center bg-zinc-50 rounded-full">
                  <i className="ri-history-line text-xl" />
                </div>
                <p className="text-[13px]">Nenhum registo de sessão ainda.</p>
                <p className="text-[11px] text-zinc-300">Faz logout e login novamente para ver o primeiro registo.</p>
              </div>
            ) : (
              sessionLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between px-6 py-3.5 hover:bg-zinc-50/60 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      log.action === 'login' ? 'bg-emerald-50' : 'bg-zinc-50'
                    }`}>
                      <i className={`text-sm ${
                        log.action === 'login' ? 'ri-login-circle-line text-emerald-500' :
                        log.action === 'logout' ? 'ri-logout-circle-line text-zinc-400' :
                        'ri-refresh-line text-zinc-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-[13px] font-medium text-zinc-700">
                        {log.action === 'login' ? 'Início de sessão' :
                         log.action === 'logout' ? 'Fim de sessão' : 'Sessão renovada'}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {log.municipio_nome && (
                          <span className="text-[11px] text-zinc-400">
                            <i className="ri-building-4-line text-xs mr-0.5" />
                            {log.municipio_nome}
                          </span>
                        )}
                        {log.ip_address && (
                          <span className="text-[11px] text-zinc-300">· {log.ip_address}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[12px] font-medium text-zinc-500">{timeAgo(log.created_at)}</p>
                    <p className="text-[10px] text-zinc-300 mt-0.5">{formatDate(log.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
